import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tag, X } from 'lucide-react';
import { api } from '../../../api/client.ts';
import { useAuth } from '../../../contexts/AuthContext';
import { Category } from '../../../types/index.ts';
import { useCategoryColorMap } from './useCategoryColors.ts';
import { CategoryTree } from './CategoryTree.tsx';
import { CategoryEditor } from './CategoryEditor.tsx';

// ─── Manage Categories modal — "Categories B" (2026-10-11) ─────────────────────────────────────
// Extracted out of Inventory.tsx (the old CategoryManager, 2527-3280). Prop contract is
// unchanged from the old component -- Inventory.tsx's own render call site needs no changes
// beyond the import.
export function CategoryManager({ categories, onClose, onChanged, initialCategoryId }: {
  categories: Category[];
  onClose: () => void;
  onChanged: () => void;
  initialCategoryId?: number | null;
}) {
  const { user } = useAuth();
  const readOnly = !['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const queryClient = useQueryClient();

  const { data: catData, refetch: refetchCats } = useQuery({
    queryKey: ['category-manager-cats'],
    queryFn: () => api.get<Category[]>('/inventory/categories'),
  });
  const managedCategories: Category[] = (catData as { data?: Category[] } | undefined)?.data ?? categories;

  const [selectedId, setSelectedId] = useState<number | null>(initialCategoryId ?? null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const categoryColorMap = useCategoryColorMap(managedCategories);

  // Keep ancestors of the selected category expanded in the tree (mirrors the old component's
  // initialCategoryId ancestor-expansion behavior).
  useEffect(() => {
    if (selectedId == null) return;
    const cat = managedCategories.find((c) => c.id === selectedId);
    if (!cat) return;
    const ancestors: number[] = [];
    let cur: Category | undefined = cat;
    while (cur?.parent_id) { ancestors.push(cur.parent_id); cur = managedCategories.find((c) => c.id === cur!.parent_id); }
    if (ancestors.length === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      ancestors.forEach((id) => next.add(id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, managedCategories.length]);

  // Prevent background scroll while this modal is open (same as the old component).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  function refreshAll() {
    queryClient.invalidateQueries({ queryKey: ['inventory-categories'] });
    void refetchCats();
    onChanged();
  }

  async function handleCreateTop() {
    if (readOnly) return;
    try {
      const res = await api.post<Category>('/inventory/categories', { name: 'New Category', parent_id: null, color: null, icon_path: 'Package' });
      const created = (res as unknown as { data?: Category })?.data;
      refreshAll();
      if (created?.id) setSelectedId(created.id);
    } catch { /* silent -- matches old component's save-failure handling (console only) */ }
  }

  const selected = selectedId != null ? managedCategories.find((c) => c.id === selectedId) ?? null : null;
  const mouseDownTargetRef = { current: null as EventTarget | null };

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => { mouseDownTargetRef.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTargetRef.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTargetRef.current = null; }}
    >
      <div
        className="modal cat-manager-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1080px, 100vw - 32px)',
          height: 'min(720px, 100vh - 32px)',
          maxWidth: 'none',
          borderRadius: 22,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag size={18} className="modal-icon-intro" />
            Manage categories
          </h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <CategoryTree
            categories={managedCategories}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreateTop={() => void handleCreateTop()}
            categoryColorMap={categoryColorMap}
            readOnly={readOnly}
            expandedIds={expandedIds}
            setExpandedIds={setExpandedIds}
          />

          {selected ? (
            <CategoryEditor
              key={selected.id}
              category={selected}
              categories={managedCategories}
              categoryColorMap={categoryColorMap}
              readOnly={readOnly}
              onSelect={setSelectedId}
              onChanged={refreshAll}
              onDeleted={() => setSelectedId(selected.parent_id ?? null)}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--text-muted)' }}>
              <Tag size={40} style={{ opacity: 0.3 }} />
              <p style={{ margin: 0, textAlign: 'center', fontSize: '0.875rem' }}>
                Select a category to edit,<br />or add a new one
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .cat-manager-modal { animation: catManagerIn 0.18s ease; }
        @keyframes catManagerIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        @media (prefers-reduced-motion: reduce) {
          .cat-manager-modal { animation: none; }
        }
      `}</style>
    </div>,
    document.body
  );
}
